import React, { useEffect, useRef, useState } from 'react';

import Icon from "./Icon.jsx";

/**
 * Button component
 *
 * Renders a styled button with optional text, leading/trailing icons, or icon-only mode.
 *
 * @param {object} props
 * @param {string} [props.id] - The unique HTML element ID for the button.
 *
 * @param {string} [props.hierarchy="primary"]
 *   Visual style variant of the button.
 *   Allowed: "primary" | "secondary" | "tertiary" | "danger"
 *
 * @param {string} [props.size="sm"]
 *   Button size and icon size mapping.
 *   Allowed: "sm" | "md"
 *
 * @param {string} [props.text]
 *   Text content to display inside the button.
 *
 * @param {string} [props.icon]
 *   Icon name for an icon-only button.
 *   When provided **without** text → button becomes icon-only (`btn-icon-{size}`).
 *
 * @param {string} [props.leadingIcon]
 *   Icon name to render before the text.
 *
 * @param {string} [props.trailingIcon]
 *   Icon name to render after the text.
 *
 * @param {boolean} [props.disabled=false]
 *   Disables the button and prevents user interaction.
 *
 * @param {string} [props.type="button"]
 *   The button type attribute. Example: "button", "submit", "reset".
 *
 * @param {string} [props.form]
 *   Associates the button with a specific `<form>` by ID.
 *
 * @param {string} [props.className]
 *   Additional CSS classes to append to the button element.
 *   React equivalent of `extra_classes` or `button_class`.
 *
 * @param {string} [props.title]
 *   Native browser tooltip text.
 *
 * @param {function} [props.onClick]
 *   Click handler function.
 *
 * @param {object} [props.rest]
 *   Any additional attributes forwarded to the `<button>` element.
 */
export default function Button({
    id,
    hierarchy = "primary",
    size = "sm",
    text = "",
    icon,
    leadingIcon,
    trailingIcon,
    disabled = false,
    form,
    type = "button",
    className = "",
    title,
    onClick,
    ...rest
}) {
    const buttonRef = useRef(null);
    const isMountedRef = useRef(true);

    const hasText = Boolean(text);
    const hasAnyIcon = Boolean(icon || leadingIcon || trailingIcon);
    const hasOnlyIcon = hasAnyIcon && !hasText;

    const [isProcessing, setIsProcessing] = useState(false);
    const processingRef = useRef(false);

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    async function handleClick(e) {
        if (processingRef.current) return;

        if (buttonRef.current) {
            buttonRef.current.blur();
        }
        if (onClick) {
            processingRef.current = true;
            let isAsyncResult = false;

            try {
                const result = onClick(e);
                isAsyncResult = typeof result?.then === "function";

                if (isAsyncResult) {
                    if (isMountedRef.current) {
                        setIsProcessing(true);
                    }

                    await result;
                }
            } finally {
                if (isAsyncResult && isMountedRef.current) {
                    setIsProcessing(false);
                }

                processingRef.current = false;
            }
        }
    }

    function handlePointerDown(event) {
        event.stopPropagation();
    }

    function handlePointerUp(event) {
        event.stopPropagation();
    }

    function buildButtonClassName({ hierarchy, size, hasOnlyIcon, className }) {
        const classes = [
            "btn",
            `btn-${hierarchy}`,
            hasOnlyIcon ? `btn-icon-${size}` : `btn-${size}`,
        ];

        if (className) classes.push(className);

        return classes.join(" ");
    }

    const buttonClassName = buildButtonClassName({
        hierarchy,
        size,
        hasOnlyIcon,
        className,
    });

    function renderIcon(name) {
        if (!name) return null;
        return <Icon name={name} size={size} />;
    }

    return (
        <button
            ref={buttonRef}
            id={id}
            type={type}
            className={buttonClassName}
            title={title}
            disabled={disabled || isProcessing}
            form={form}
            onClick={handleClick}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            {...rest}
        >
            {renderIcon(icon)}
            {renderIcon(leadingIcon)}
            {hasText && <span className="px-2">{text}</span>}
            {renderIcon(trailingIcon)}
        </button>
    );
}
